import { useState, useRef, useEffect } from 'react';
import html2canvas from 'html2canvas';
import { jsPDF } from 'jspdf';
import Icon from './Icon';
import Modal from './Modal';
import { Storage } from '../storage';

// ─── CAROUSEL ESTIMATE HELPER ─────────────────────────────────────────────────

const getCarouselEstimate = (timeline) => {
  if (!timeline || !Array.isArray(timeline.events)) return null;

  const MS_PER_SLIDE    = 100;
  const PHOTO_FETCH_MS  = 3000;
  const ZIP_OVERHEAD_MS = 500;

  const slideCount = Math.min(timeline.events.length, 19) + 1;
  const photoCount = timeline.events.filter(ev => ev.photo).length;
  const hasPhotos  = photoCount > 0;

  const estimatedMs   = (slideCount * MS_PER_SLIDE) + (hasPhotos ? PHOTO_FETCH_MS : 0) + ZIP_OVERHEAD_MS;
  const estimatedSecs = Math.ceil(estimatedMs / 1000);

  if (estimatedSecs <= 4)  return 'about a few seconds';
  if (estimatedSecs <= 9)  return 'about 5–10 seconds';
  if (estimatedSecs <= 19) return 'about 10–20 seconds';
  return 'about 20–30 seconds';
};

// ─── EXPORT MENU COMPONENT ────────────────────────────────────────────────────
//
//  Dropdown with three export actions: PNG, PDF, and Embed Code.
//  PNG and PDF use html2canvas to capture the timeline canvas at 2x scale.
//  Embed Code generates an <iframe> snippet pointing to /embed/:timelineId.
//
//  Props:
//    timeline        {object}   the timeline being exported
//    captureRef      {ref}      ref attached to the wrapper div around <TimelineCanvas>
//    buttonColor     {string}   CSS color for the trigger button (defaults to var(--accent))
//    buttonBorder    {string}   CSS color for button border
//

const ExportMenu = ({
  timeline,
  captureRef,
  buttonColor       = 'var(--accent)',
  buttonBorder      = 'var(--border2)',
  formats           = ['png', 'pdf', 'carousel', 'embed'],
  onBeforeCapture   = null,  // async () => void — called before html2canvas runs
  onAfterCapture    = null,  // () => void — called in finally after capture
}) => {
  const [open,            setOpen]            = useState(false);
  const [loading,         setLoading]         = useState(false);
  const [loadingMsg,      setLoadingMsg]       = useState('');
  const [embedOpen,       setEmbedOpen]        = useState(false);
  const [embedCopied,     setEmbedCopied]      = useState(false);
  const [carouselLoading, setCarouselLoading]  = useState(false);
  const [elapsedSecs,     setElapsedSecs]       = useState(0);
  const menuRef         = useRef(null);
  const elapsedInterval = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  // ── Color normalizer for html2canvas ──────────────────────────────────────
  //
  // html2canvas 1.4.x can't parse the CSS `color()` function (e.g. Chrome
  // on wide-gamut/P3 displays reports computed colors as "color(display-p3 …)")
  // or `color-mix()`.  We convert any such value to plain rgb() using an
  // offscreen canvas before passing the clone to html2canvas.

  const normalizeColors = (clonedDoc) => {
    const offscreen = document.createElement('canvas');
    offscreen.width = offscreen.height = 1;
    const ctx = offscreen.getContext('2d');

    const toRGB = (val) => {
      if (!val || typeof val !== 'string') return null;
      const trimmed = val.trim();
      if (!/^color\s*\(|^color-mix\s*\(|^oklch\s*\(|^lch\s*\(|^lab\s*\(/i.test(trimmed)) return null;
      try {
        ctx.clearRect(0, 0, 1, 1);
        ctx.fillStyle = trimmed;
        ctx.fillRect(0, 0, 1, 1);
        const [r, g, b, a] = ctx.getImageData(0, 0, 1, 1).data;
        return a < 255
          ? `rgba(${r},${g},${b},${+(a / 255).toFixed(3)})`
          : `rgb(${r},${g},${b})`;
      } catch { return null; }
    };

    // Fix inline styles
    clonedDoc.querySelectorAll('[style]').forEach(el => {
      const s = el.style;
      for (const prop of [...s]) {
        const fixed = toRGB(s.getPropertyValue(prop));
        if (fixed) s.setProperty(prop, fixed, s.getPropertyPriority(prop));
      }
    });

    // Fix stylesheet rules (skip cross-origin sheets)
    for (const sheet of clonedDoc.styleSheets) {
      let rules;
      try { rules = sheet.cssRules; } catch { continue; }
      for (const rule of rules) {
        if (!rule.style) continue;
        for (const prop of [...rule.style]) {
          const fixed = toRGB(rule.style.getPropertyValue(prop));
          if (fixed) rule.style.setProperty(prop, fixed, rule.style.getPropertyPriority(prop));
        }
      }
    }
  };

  // ── Shared capture helper ─────────────────────────────────────────────────

  const capture = async () => {
    const el = captureRef?.current;
    if (!el) throw new Error('Timeline canvas element not found.');

    // Let the layout prepare (e.g. Swimlane switches to overview zoom).
    await onBeforeCapture?.();

    // Wait for custom fonts to be ready so Playfair Display renders correctly.
    await document.fonts.ready;

    // Temporarily expand any clipping overflow so the full canvas is captured.
    const canvasOuter = el.firstElementChild;
    const origOverflow = canvasOuter?.style.overflow;
    const origRadius   = canvasOuter?.style.borderRadius;
    if (canvasOuter) {
      canvasOuter.style.overflow     = 'visible';
      canvasOuter.style.borderRadius = '0';
    }

    // Expand inner horizontal scroll containers so the full width is captured.
    const scrollEl = el.querySelector('[style*="overflow"]');
    const origScrollOverflow = scrollEl?.style.overflowX;
    const origScrollWidth    = scrollEl?.style.width;
    if (scrollEl) {
      scrollEl.style.overflowX = 'visible';
      scrollEl.style.width     = `${scrollEl.scrollWidth}px`;
    }

    try {
      return await html2canvas(el, {
        scale:           2,
        useCORS:         true,
        allowTaint:      true,
        backgroundColor: null,
        width:           el.scrollWidth,
        height:          el.scrollHeight,
        scrollX:         0,
        scrollY:         0,
        onclone: (clonedDoc) => {
          normalizeColors(clonedDoc);

          // Hide all UI controls so the export shows only timeline content.
          const style = clonedDoc.createElement('style');
          style.textContent = `
            .no-export,
            .sp-prog-bar, .sp-ctrl-bar, .sp-toc, .sp-ch-nav,
            .sp-top-btn, .sp-sel-tip, .sp-ch-actions, .sp-photo-prompt,
            .sl-zoom-bar, .tree-ctrl-bar,
            .fs-cinematic-bar, .fs-reel-wrap, .fs-dots,
            .fs-nav-btn, .fs-counter, .fs-actions,
            .fs-sources-badge, .fs-photo-prompt, .fs-present-bar
            { display: none !important; }
          `;
          clonedDoc.head.appendChild(style);
        },
      });
    } finally {
      if (canvasOuter) {
        canvasOuter.style.overflow     = origOverflow     ?? '';
        canvasOuter.style.borderRadius = origRadius       ?? '';
      }
      if (scrollEl) {
        scrollEl.style.overflowX = origScrollOverflow ?? '';
        scrollEl.style.width     = origScrollWidth    ?? '';
      }
      onAfterCapture?.();
    }
  };

  const safeFilename = (ext) =>
    `${(timeline.title || 'timeline').replace(/[^a-z0-9]+/gi, '_').replace(/^_+|_+$/g, '')}.${ext}`;

  // ── PNG export ────────────────────────────────────────────────────────────

  const handlePNG = async () => {
    setOpen(false);
    setLoading(true);
    setLoadingMsg('Capturing timeline…');
    try {
      const canvas = await capture();
      const link = document.createElement('a');
      link.download = safeFilename('png');
      link.href     = canvas.toDataURL('image/png');
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (err) {
      alert(`PNG export failed: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  // ── Add a captured canvas to the PDF, slicing across pages as needed ────

  const addCanvasToPdf = (pdf, canvas) => {
    const pageW  = pdf.internal.pageSize.getWidth();
    const pageH  = pdf.internal.pageSize.getHeight();
    const MARGIN = 24;
    const TOP    = 48;

    const imgW   = pageW - MARGIN * 2;
    const imgH   = (canvas.height / canvas.width) * imgW;
    const availH = pageH - TOP - MARGIN;

    if (imgH <= availH) {
      pdf.addImage(canvas.toDataURL('image/png'), 'PNG', MARGIN, TOP, imgW, imgH);
    } else {
      const scale      = canvas.width / imgW;
      const pageSliceH = availH * scale;
      let yOffset = 0;
      let first   = true;

      while (yOffset < canvas.height) {
        if (!first) pdf.addPage();
        const sliceH    = Math.min(pageSliceH, canvas.height - yOffset);
        const slice     = document.createElement('canvas');
        slice.width     = canvas.width;
        slice.height    = sliceH;
        slice.getContext('2d').drawImage(canvas, 0, -yOffset);
        const topY      = first ? TOP : MARGIN;
        const renderedH = sliceH / scale;
        pdf.addImage(slice.toDataURL('image/png'), 'PNG', MARGIN, topY, imgW, renderedH);
        yOffset += sliceH;
        first = false;
      }
    }
  };

  // ── PDF export ────────────────────────────────────────────────────────────

  const handlePDF = async () => {
    setOpen(false);
    setLoading(true);
    try {
      const pdf    = new jsPDF({ orientation: 'landscape', unit: 'pt' });
      const pageW  = pdf.internal.pageSize.getWidth();
      const MARGIN = 24;

      // ── Page 1: Timeline canvas ──────────────────────────────────────────
      setLoadingMsg('Capturing timeline…');
      const timelineCanvas = await capture();

      pdf.setFont('helvetica', 'bold');
      pdf.setFontSize(18);
      pdf.setTextColor(40, 40, 40);
      pdf.text(timeline.title || 'Timeline', MARGIN, 36);

      addCanvasToPdf(pdf, timelineCanvas);

      // ── Page(s): All Events ──────────────────────────────────────────────
      const events = [...(timeline.events || [])].sort((a, b) =>
        (a.startDate || '').localeCompare(b.startDate || '')
      );

      if (events.length > 0) {
        setLoadingMsg('Adding events…');

        const pageH  = pdf.internal.pageSize.getHeight();
        const stripTags = (html) => (html || '').replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();

        pdf.addPage();

        let y = MARGIN;

        // Section heading
        pdf.setFont('helvetica', 'bold');
        pdf.setFontSize(16);
        pdf.setTextColor(40, 40, 40);
        pdf.text(`All Events (${events.length})`, MARGIN, y + 14);
        y += 24;

        // Divider
        pdf.setDrawColor(180, 180, 180);
        pdf.setLineWidth(0.5);
        pdf.line(MARGIN, y, pageW - MARGIN, y);
        y += 14;

        events.forEach((ev) => {
          // Estimate block height: title(14) + date(12) + up to 3 desc lines(10 each) + gap(10)
          const desc  = stripTags(ev.description);
          const descLines = desc
            ? pdf.splitTextToSize(desc, pageW - MARGIN * 2).slice(0, 3)
            : [];
          const blockH = 14 + 12 + descLines.length * 10 + 14;

          if (y + blockH > pageH - MARGIN) {
            pdf.addPage();
            y = MARGIN;
          }

          // Title
          pdf.setFont('helvetica', 'bold');
          pdf.setFontSize(11);
          pdf.setTextColor(30, 30, 30);
          const title = (ev.title || 'Untitled').slice(0, 80);
          pdf.text(title, MARGIN, y);
          y += 14;

          // Date range
          pdf.setFont('helvetica', 'normal');
          pdf.setFontSize(9);
          pdf.setTextColor(100, 100, 100);
          pdf.text(ev.endDate && ev.endDate !== ev.startDate ? `${ev.startDate}  →  ${ev.endDate}` : ev.startDate, MARGIN, y);
          y += 12;

          // Description
          if (descLines.length > 0) {
            pdf.setFontSize(9);
            pdf.setTextColor(70, 70, 70);
            pdf.text(descLines, MARGIN, y);
            y += descLines.length * 10;
          }

          // Gap + separator
          y += 6;
          pdf.setDrawColor(220, 220, 220);
          pdf.setLineWidth(0.25);
          pdf.line(MARGIN, y, pageW - MARGIN, y);
          y += 8;
        });
      }

      pdf.save(safeFilename('pdf'));
    } catch (err) {
      alert(`PDF export failed: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  // ── Embed code ────────────────────────────────────────────────────────────

  const isPublic   = timeline.privacy === 'public';
  const embedUrl   = `${window.location.origin}/embed/${timeline.id}`;
  const safeTitle  = (timeline.title || 'Timeline').replace(/"/g, '&quot;');
  const embedCode  =
    `<iframe\n  src="${embedUrl}"\n  width="100%"\n  height="500"\n  style="border:none;border-radius:12px;"\n  title="${safeTitle}"\n  allowfullscreen\n></iframe>`;

  const handleEmbedOpen = () => {
    if (!isPublic) return;
    setOpen(false);
    setEmbedOpen(true);
  };

  const copyEmbed = () => {
    navigator.clipboard?.writeText(embedCode).catch(() => {});
    setEmbedCopied(true);
    setTimeout(() => setEmbedCopied(false), 2000);
  };

  // ── Carousel export ───────────────────────────────────────────────────────

  const handleCarouselExport = async () => {
    setOpen(false);
    setCarouselLoading(true);
    setElapsedSecs(0);

    elapsedInterval.current = setInterval(() => {
      setElapsedSecs(s => s + 1);
    }, 1000);

    try {
      await Storage.exportCarousel(timeline.id, timeline.title);
    } catch (err) {
      alert(`Carousel export failed: ${err.message}`);
    } finally {
      clearInterval(elapsedInterval.current);
      elapsedInterval.current = null;
      setCarouselLoading(false);
      setElapsedSecs(0);
    }
  };

  useEffect(() => {
    return () => {
      if (elapsedInterval.current) clearInterval(elapsedInterval.current);
    };
  }, []);

  // ── Shared menu-item style ────────────────────────────────────────────────

  const itemStyle = (disabled = false) => ({
    display: 'flex', alignItems: 'center', gap: 10,
    width: '100%', padding: '10px 16px',
    background: 'none', border: 'none',
    color: disabled ? 'var(--text3)' : 'var(--text2)',
    fontSize: 13, textAlign: 'left',
    cursor: disabled ? 'not-allowed' : 'pointer',
    transition: 'background 0.15s, color 0.15s',
  });

  const itemHover = (e, disabled) => {
    if (disabled) return;
    e.currentTarget.style.background = 'var(--bg3)';
    e.currentTarget.style.color      = 'var(--text)';
  };
  const itemLeave = (e, disabled) => {
    e.currentTarget.style.background = 'none';
    e.currentTarget.style.color      = disabled ? 'var(--text3)' : 'var(--text2)';
  };

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <>
      {/* Trigger + dropdown */}
      <div ref={menuRef} style={{ position: 'relative' }}>
        <button
          onClick={() => setOpen(v => !v)}
          aria-haspopup="menu"
          aria-expanded={open}
          style={{
            display: 'flex', alignItems: 'center', gap: 7,
            background: `${buttonColor}22`,
            color: buttonColor,
            border: `1px solid ${buttonBorder}`,
            borderRadius: 'var(--radius)', padding: '7px 14px',
            cursor: 'pointer', fontSize: 13, fontWeight: 500,
            transition: 'all 0.2s',
          }}
        >
          <Icon name="download" size={14} color={buttonColor} />
          Export
        </button>

        {open && (
          <div
            role="menu"
            style={{
              position: 'absolute', right: 0, top: 'calc(100% + 6px)',
              background: 'var(--surface)', border: '1px solid var(--border2)',
              borderRadius: 'var(--radius)', boxShadow: 'var(--shadow)',
              overflow: 'hidden', minWidth: 210, zIndex: 200,
            }}
          >
            {/* PNG */}
            {formats.includes('png') && (
              <button
                role="menuitem"
                onClick={handlePNG}
                style={itemStyle()}
                onMouseEnter={e => itemHover(e, false)}
                onMouseLeave={e => itemLeave(e, false)}
              >
                <Icon name="image" size={15} />
                Export as PNG
              </button>
            )}

            {/* PDF */}
            {formats.includes('pdf') && (
              <button
                role="menuitem"
                onClick={handlePDF}
                style={itemStyle()}
                onMouseEnter={e => itemHover(e, false)}
                onMouseLeave={e => itemLeave(e, false)}
              >
                <Icon name="fileText" size={15} />
                Export as PDF
              </button>
            )}

            {/* Carousel */}
            {formats.includes('carousel') && (
              <div>
                <button
                  role="menuitem"
                  onClick={handleCarouselExport}
                  disabled={carouselLoading}
                  style={itemStyle(carouselLoading)}
                  onMouseEnter={e => itemHover(e, carouselLoading)}
                  onMouseLeave={e => itemLeave(e, carouselLoading)}
                >
                  <Icon name="image" size={15} />
                  {carouselLoading
                    ? `Generating… ${elapsedSecs}s`
                    : '📱 Export Carousel'}
                </button>
                <div style={{ margin: '0 16px 8px' }}>
                  {carouselLoading ? (
                    <div style={{ marginTop: 8 }}>
                      <p style={{ fontSize: 11, color: 'var(--text3)', lineHeight: 1.6, margin: 0 }}>
                        {(timeline?.events?.filter(ev => ev.photo).length ?? 0) > 0
                          ? 'Fetching photos and rendering slides…'
                          : 'Rendering slides…'}
                      </p>
                      <p style={{ fontSize: 11, color: 'var(--text3)', lineHeight: 1.6, margin: '4px 0 0' }}>
                        Your download will start automatically when ready.
                      </p>
                    </div>
                  ) : (
                    (() => {
                      const estimate   = getCarouselEstimate(timeline);
                      const photoCount = timeline?.events?.filter(ev => ev.photo).length ?? 0;
                      const slideCount = Math.min(timeline?.events?.length ?? 0, 19) + 1;
                      return (
                        <div style={{ marginTop: 8 }}>
                          <p style={{ fontSize: 11, color: 'var(--text3)', lineHeight: 1.6, margin: 0 }}>
                            {slideCount} slide{slideCount !== 1 ? 's' : ''}
                            {photoCount > 0 ? ` · ${photoCount} photo${photoCount !== 1 ? 's' : ''}` : ' · text only'}
                            {estimate ? ` · est. ${estimate}` : ''}
                          </p>
                          <p style={{ fontSize: 11, color: 'var(--text3)', lineHeight: 1.6, margin: '4px 0 0' }}>
                            Downloads a zip of 1080×1080 slides ready to post on Instagram, LinkedIn, Facebook, and X.
                          </p>
                        </div>
                      );
                    })()
                  )}
                </div>
              </div>
            )}

            {formats.includes('embed') && formats.some(f => f !== 'embed') && (
              <div style={{ height: 1, background: 'var(--border)', margin: '4px 0' }} />
            )}

            {/* Embed — disabled for private timelines */}
            {formats.includes('embed') && (
              <button
                role="menuitem"
                onClick={handleEmbedOpen}
                disabled={!isPublic}
                title={!isPublic ? 'Make this timeline public to enable embedding' : undefined}
                style={itemStyle(!isPublic)}
                onMouseEnter={e => itemHover(e, !isPublic)}
                onMouseLeave={e => itemLeave(e, !isPublic)}
              >
              <Icon name="code" size={15} color={isPublic ? 'currentColor' : 'var(--text3)'} />
              Get Embed Code
              {!isPublic && (
                <span style={{ marginLeft: 'auto', fontSize: 11, fontStyle: 'italic' }}>
                  Public only
                </span>
              )}
            </button>
            )}
          </div>
        )}
      </div>

      {/* Loading overlay */}
      {loading && (
        <div
          role="status"
          aria-label={loadingMsg}
          style={{
            position: 'fixed', inset: 0,
            background: 'rgba(0,0,0,0.55)', backdropFilter: 'blur(3px)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            zIndex: 9999,
          }}
        >
          <div style={{
            background: 'var(--surface)', border: '1px solid var(--border)',
            borderRadius: 'var(--radius-lg)', padding: '28px 44px',
            textAlign: 'center', boxShadow: 'var(--shadow)',
          }}>
            <div className="export-spinner" aria-hidden="true" />
            <p style={{ color: 'var(--text2)', fontSize: 14, marginTop: 16 }}>{loadingMsg}</p>
          </div>
        </div>
      )}

      {/* Embed code modal */}
      <Modal
        open={embedOpen}
        onClose={() => { setEmbedOpen(false); setEmbedCopied(false); }}
        title="Embed Timeline"
        width={540}
      >
        <p style={{ fontSize: 14, color: 'var(--text2)', marginBottom: 16, lineHeight: 1.6 }}>
          Paste this snippet into any webpage to embed <strong style={{ color: 'var(--text)' }}>{timeline.title}</strong> as an interactive iframe.
        </p>

        {/* Code block */}
        <div style={{
          background: 'var(--bg3)', border: '1px solid var(--border)',
          borderRadius: 'var(--radius)', overflow: 'hidden', marginBottom: 12,
        }}>
          <div style={{
            padding: '6px 14px', borderBottom: '1px solid var(--border)',
            display: 'flex', alignItems: 'center', gap: 8,
          }}>
            <span style={{ fontSize: 11, color: 'var(--text3)', fontFamily: 'monospace', letterSpacing: '0.04em' }}>HTML</span>
          </div>
          <pre style={{
            padding: '14px 16px', margin: 0,
            fontSize: 12, lineHeight: 1.75, color: 'var(--accent2)',
            fontFamily: 'monospace', overflowX: 'auto',
            whiteSpace: 'pre-wrap', wordBreak: 'break-all',
          }}>
            {embedCode}
          </pre>
        </div>

        <p style={{ fontSize: 12, color: 'var(--text3)', marginBottom: 20, lineHeight: 1.5 }}>
          Embed URL:{' '}
          <a
            href={embedUrl}
            target="_blank"
            rel="noopener noreferrer"
            style={{ color: 'var(--accent)', wordBreak: 'break-all' }}
          >
            {embedUrl}
          </a>
        </p>

        <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
          <button
            className="btn-ghost"
            onClick={() => { setEmbedOpen(false); setEmbedCopied(false); }}
          >
            Close
          </button>
          <button
            className="btn-primary"
            onClick={copyEmbed}
            style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 148 }}
          >
            <Icon name={embedCopied ? 'check' : 'copy'} size={14} />
            {embedCopied ? 'Copied!' : 'Copy Snippet'}
          </button>
        </div>
      </Modal>
    </>
  );
};

export default ExportMenu;
